/** The initial project state, typed lookups and compaction. Linear scans: project states are small. */

import { prRunIdsOf } from "./pr-runs"
import type {
  Actor,
  Agent,
  AgentId,
  Area,
  Artifact,
  Comment,
  Edge,
  EntityRef,
  Goal,
  Ids,
  Item,
  ItemId,
  ItemStatus,
  Kpi,
  Member,
  MemoryItem,
  Policy,
  Principal,
  ProjectState,
  Proposal,
  Run,
  RunId,
  UserId,
} from "./types"

export const initialState: ProjectState = {
  v: 2,
  seq: 0 as Ids["Seq"],
  planVersion: 0,
  project: null,
  members: [],
  mayor: null,
  policies: [],
  areas: [],
  agents: [],
  goals: [],
  kpis: [],
  items: [],
  edges: [],
  proposals: [],
  runs: [],
  openAsks: [],
  artifacts: [],
  memory: [],
  refCounters: { G: 0, K: 0, I: 0, T: 0, P: 0, R: 0 },
}

export const itemOf = (s: ProjectState, id: ItemId): Item | undefined => s.items.find((t) => t.id === id)
export const goalOf = (s: ProjectState, id: Ids["GoalId"]): Goal | undefined =>
  s.goals.find((g) => g.id === id)
export const kpiOf = (s: ProjectState, id: Ids["KpiId"]): Kpi | undefined => s.kpis.find((k) => k.id === id)
export const areaOf = (s: ProjectState, id: Ids["AreaId"]): Area | undefined =>
  s.areas.find((a) => a.id === id)
export const agentOf = (s: ProjectState, id: AgentId): Agent | undefined => s.agents.find((a) => a.id === id)
export const edgeOf = (s: ProjectState, id: Ids["EdgeId"]): Edge | undefined =>
  s.edges.find((e) => e.id === id)
export const proposalOf = (s: ProjectState, id: Ids["ProposalId"]): Proposal | undefined =>
  s.proposals.find((p) => p.id === id)
export const runOf = (s: ProjectState, id: RunId): Run | undefined => s.runs.find((r) => r.id === id)
export const policyOf = (s: ProjectState, id: Ids["PolicyId"]): Policy | undefined =>
  s.policies.find((p) => p.id === id)
export const commentOf = (s: ProjectState, id: Ids["CommentId"]): Comment | undefined =>
  s.openAsks.find((c) => c.id === id)
export const artifactOf = (s: ProjectState, id: Ids["ArtifactId"]): Artifact | undefined =>
  s.artifacts.find((a) => a.id === id)
export const memoryOf = (s: ProjectState, id: Ids["MemoryItemId"]): MemoryItem | undefined =>
  s.memory.find((m) => m.id === id)
export const memberOf = (s: ProjectState, id: UserId): Member | undefined =>
  s.members.find((m) => m.userId === id)

export const leadOf = (s: ProjectState): Agent | undefined =>
  s.project?.leadAgentId
    ? agentOf(s, s.project.leadAgentId)
    : s.agents.find((a) => a.role === "lead" && a.status !== "retired")

/** The person or agent behind an actor (decision 0032: who paused an item, who asked to stop a run); null otherwise. */
export const principalOf = (a: Actor): Principal | null =>
  a._tag === "user"
    ? { _tag: "user", userId: a.userId }
    : a._tag === "agent"
      ? { _tag: "agent", agentId: a.agentId }
      : null

/**
 * Decision 0116: did a person write the item's current checks (or approve the card that set them)? Items from before
 * 0116 (no `checksBy`) count as their creator's.
 */
export const checksByPerson = (t: Pick<Item, "checksBy" | "createdBy">): boolean =>
  t.checksBy !== undefined ? t.checksBy?._tag === "user" : t.createdBy._tag === "user"

/** A workspace owner or admin (in this project's roster). */
export const isAdmin = (s: ProjectState, userId: UserId): boolean => {
  const m = memberOf(s, userId)
  return m !== undefined && (m.role === "owner" || m.role === "admin")
}

/**
 * Decision 0129 (0126 §1): this person's role in the project, or null when they are not in its roster. Workspace owners
 * and admins are admins of every project; a roster synced before 0129 (no `projectRole`) makes the project's owner an
 * admin and everyone else a member. A project `viewer` (workspace role) keeps `member` here; `authorize` limits them.
 */
export const projectRoleOf = (s: ProjectState, userId: UserId): "admin" | "member" | null => {
  const m = memberOf(s, userId)
  if (!m) return null
  if (m.role === "owner" || m.role === "admin") return "admin"
  if (m.projectRole) return m.projectRole
  return s.project?.ownerId === userId ? "admin" : "member"
}

/** Decision 0129: a project admin (its admins, and workspace owners/admins): project settings and members. */
export const isProjectAdmin = (s: ProjectState, userId: UserId): boolean =>
  projectRoleOf(s, userId) === "admin"

/** An internal invariant: the value exists (e.g. a record decide itself just created). Throws, never a rejection. */
export const must = <T>(x: T | undefined | null, what: string): T => {
  if (x === undefined || x === null) throw new Error(`core invariant violated: missing ${what}`)
  return x
}

/** done and cancelled: closed. */
export const CLOSED: ReadonlySet<ItemStatus> = new Set(["done", "cancelled"])
export const isClosed = (t: Pick<Item, "status">): boolean => CLOSED.has(t.status)
export const isOpenItem = (t: Pick<Item, "status">): boolean => !CLOSED.has(t.status)
export const isRunEnded = (r: Run): boolean => r.state._tag === "ended"
export const OPEN_PROPOSAL_STATES: ReadonlySet<string> = new Set(["open", "revision_requested", "approved"])
export const isOpenProposal = (p: Proposal): boolean => OPEN_PROPOSAL_STATES.has(p.state)

/** Any addressable record, as `{ lastSeq, ownerId }` for preconditions and ownership checks. */
export const recordOf = (
  s: ProjectState,
  ref: EntityRef,
): { readonly lastSeq: number; readonly ownerId: UserId } | undefined => {
  switch (ref._tag) {
    case "goal":
      return goalOf(s, ref.id)
    case "kpi":
      return kpiOf(s, ref.id)
    case "item":
      return itemOf(s, ref.id)
    case "area":
      return areaOf(s, ref.id)
    case "agent":
      return agentOf(s, ref.id)
    case "edge":
      return edgeOf(s, ref.id)
    case "proposal":
      return proposalOf(s, ref.id)
    case "memory":
      return memoryOf(s, ref.id)
    case "artifact":
      return artifactOf(s, ref.id)
    case "comment":
      return commentOf(s, ref.id)
    case "policy":
      return policyOf(s, ref.id)
    case "run": {
      const r = runOf(s, ref.id)
      return r ? { lastSeq: 0, ownerId: r.ownerId } : undefined
    }
    case "thread": {
      // decision 0142: a thread belongs to the project (its owner is the project's)
      const t = s.threads?.find((x) => x.id === ref.id)
      const owner = s.project?.ownerId
      return t && owner ? { lastSeq: t.lastSeq, ownerId: owner } : undefined
    }
  }
}

/** Live (not ended) worker runs supervised by `agentId` (the owner agent of their items). */
export const liveWorkerRuns = (s: ProjectState, agentId: AgentId): ReadonlyArray<Run> =>
  s.runs.filter((r) => r.kind === "worker" && r.agentId === agentId && !isRunEnded(r))

const DAY = 86_400_000

/**
 * Snapshot compaction (v2, decision 0036 §2.13), the v1 rules in item terms: drops proposals resolved more than 7 days
 * before `asOf`; ended runs other than the latest per item and per agent; and cancelled items that carry
 * `legacy.intake` (old resolved intake) updated more than 7 days before `asOf`. Items created after the cutover are never
 * dropped. History stays in the SQL projections and the log.
 */
export const compact = (s: ProjectState, asOf: number): ProjectState => {
  const proposals = s.proposals.filter(
    (p) => isOpenProposal(p) || (p.resolution?.at ?? p.updatedAt) >= asOf - 7 * DAY,
  )
  const keep = new Set<RunId>()
  const latest = new Map<string, Run>()
  for (const r of s.runs) {
    if (!isRunEnded(r)) {
      keep.add(r.id)
      continue
    }
    for (const key of [r.itemId ? `t:${r.itemId}` : null, r.agentId ? `a:${r.agentId}` : null]) {
      if (key === null) continue
      const cur = latest.get(key)
      if (!cur || cur.requestedAt <= r.requestedAt) latest.set(key, r)
    }
  }
  for (const r of latest.values()) keep.add(r.id)
  // decision 0067 (TRI-01): a PR that still matters keeps the runs its reads need (pr-runs.ts)
  for (const id of prRunIdsOf(s, asOf)) keep.add(id)
  const items = s.items.filter(
    (x) => !(x.status === "cancelled" && x.legacy?.intake !== undefined && x.updatedAt < asOf - 7 * DAY),
  )
  return { ...s, proposals, items, runs: s.runs.filter((r) => keep.has(r.id)) }
}
